// Shared customer-segment definitions.
// Mirrors backend/src/lib/segments.ts so the labels, colours and thresholds
// shown in the UI always line up with the rules the server applies.

export type SegmentKey =
  | 'NPC'
  | 'dailyCelebrations'
  | 'notPurchased1Year'
  | 'inactive'
  | 'idle'
  | 'VIP'
  | 'ideal';

export interface SegmentMeta {
  key: SegmentKey;
  label: string;
  shortLabel: string;
  description: string;
  bgClass: string;
  badgeClass: string;
  dotClass: string;
  /** Manual tag that forces this segment (mirrors backend MANUAL_TAG_FOR_SEGMENT). */
  manualTag: string;
}

export const SEGMENTS: SegmentMeta[] = [
  {
    key: 'NPC',
    label: 'Non-Purchase Customer',
    shortLabel: 'NPC',
    description: 'Visited the store but never made a purchase',
    bgClass: 'bg-amber-50 border-amber-200',
    badgeClass: 'bg-amber-100 text-amber-800',
    dotClass: 'bg-amber-500',
    manualTag: 'NPC',
  },
  {
    key: 'dailyCelebrations',
    label: 'Daily Celebrations',
    shortLabel: 'Celebration today',
    description: 'Birthday or anniversary falls on today',
    bgClass: 'bg-pink-50 border-pink-200',
    badgeClass: 'bg-pink-100 text-pink-800',
    dotClass: 'bg-pink-500',
    manualTag: 'CELEBRATION',
  },
  {
    key: 'notPurchased1Year',
    label: 'Not Purchased in 1 Year',
    shortLabel: 'No purchase 1yr',
    description: 'Has bought before, but not in the last 12 months',
    bgClass: 'bg-orange-50 border-orange-200',
    badgeClass: 'bg-orange-100 text-orange-800',
    dotClass: 'bg-orange-500',
    manualTag: 'NOT_PURCHASED_1YR',
  },
  {
    key: 'inactive',
    label: 'Inactive Customers',
    shortLabel: 'Inactive',
    description: 'No interaction of any kind for over 6 months',
    bgClass: 'bg-gray-50 border-gray-200',
    badgeClass: 'bg-gray-100 text-gray-700',
    dotClass: 'bg-slate-400',
    manualTag: 'INACTIVE',
  },
  {
    key: 'idle',
    label: 'Idle Customers',
    shortLabel: 'Idle',
    description: 'Abandoned quote with no follow-up for 30+ days',
    bgClass: 'bg-red-50 border-red-200',
    badgeClass: 'bg-red-100 text-red-800',
    dotClass: 'bg-red-500',
    manualTag: 'IDLE',
  },
  {
    key: 'VIP',
    label: 'VIP Customers',
    shortLabel: 'VIP',
    description: 'Lifetime billing over ₹5,00,000 or 100g+ gold',
    bgClass: 'bg-yellow-50 border-yellow-300',
    badgeClass: 'bg-yellow-100 text-yellow-800',
    dotClass: 'bg-yellow-500',
    manualTag: 'VIP',
  },
  {
    key: 'ideal',
    label: 'Ideal Customers',
    shortLabel: 'Ideal',
    description: 'Repeat + recent + high value: 2+ purchases, active in 6 months, ₹5,00,000+ lifetime',
    bgClass: 'bg-emerald-50 border-emerald-200',
    badgeClass: 'bg-emerald-100 text-emerald-800',
    dotClass: 'bg-emerald-500',
    manualTag: 'IDEAL',
  },
];

/** Manual tag -> its segment meta, for rendering tag chips on customer cards. */
export const MANUAL_TAG_MAP: Record<string, SegmentMeta> = Object.fromEntries(
  SEGMENTS.map((s) => [s.manualTag, s])
) as Record<string, SegmentMeta>;

/** Display order for the manual tag buttons. */
export const MANUAL_TAG_ORDER = SEGMENTS.map((s) => s.manualTag);

export const SEGMENT_MAP: Record<string, SegmentMeta> = Object.fromEntries(
  SEGMENTS.map((s) => [s.key, s])
) as Record<string, SegmentMeta>;

export const segmentMeta = (key: string): SegmentMeta =>
  SEGMENT_MAP[key] || {
    key: key as SegmentKey,
    label: key,
    shortLabel: key,
    description: '',
    bgClass: 'bg-slate-50 border-slate-200',
    badgeClass: 'bg-slate-100 text-slate-800',
    dotClass: 'bg-slate-400',
  };

// Kept in sync with backend/src/lib/segments.ts (VIP/ideal thresholds).
export const VIP_BILLING_THRESHOLD = 500000;
export const IDEAL_MIN_PURCHASES = 2;
